import React, { useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MapEmbed from '../components/MapEmbed';
import { MapPin, Phone, Mail, User, Send } from 'lucide-react';
import { company } from '../mock';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing fields', description: 'Please fill in name, email and message.' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: 'Message sent!', description: 'Thanks for contacting us. We will get back to you shortly.' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="page-hero" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1423666639041-f56000c27a9a?q=80&w=1600&auto=format&fit=crop)', paddingTop: '80px' }}>
        <div className="text-center max-w-3xl mx-auto px-6">
          <p className="uppercase text-xs tracking-[0.4em] mb-3 opacity-80">Get In Touch</p>
          <h1 className="text-4xl md:text-6xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>Contact Us</h1>
          <div className="mx-auto w-16 h-[2px] bg-white/70 mt-5" />
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10">
          <div>
            <p className="text-[color:var(--brand)] uppercase tracking-[0.4em] text-xs mb-3">Address</p>
            <h2 className="text-3xl font-light mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>We’d love to hear from you</h2>
            <p className="text-[color:var(--muted)] mb-8">Reach out for quotes, partnerships or any queries. Our team will respond within one business day.</p>
            <div className="space-y-5">
              <div className="flex gap-4 items-start">
                <span className="w-11 h-11 rounded-full bg-[color:var(--brand-soft)] text-[color:var(--brand)] inline-flex items-center justify-center"><MapPin size={18} /></span>
                <div>
                  <div className="font-semibold">Head Office</div>
                  <div className="text-[color:var(--muted)]">7/33-34, Sharda Sadan, Near Central Bus Stand, Railway Station Road, Surat - 395003, Gujarat, India</div>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <span className="w-11 h-11 rounded-full bg-[color:var(--brand-soft)] text-[color:var(--brand)] inline-flex items-center justify-center"><MapPin size={18} /></span>
                <div>
                  <div className="font-semibold">Forwarding Office</div>
                  <div className="text-[color:var(--muted)]">34-A/1, Adani Port Road, Hazira, Choryashi, Surat - 394270, Gujarat, India</div>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <span className="w-11 h-11 rounded-full bg-[color:var(--brand-soft)] text-[color:var(--brand)] inline-flex items-center justify-center"><Phone size={18} /></span>
                <div>
                  <div className="font-semibold">Phone</div>
                  <a href={`tel:${company.phoneRaw}`} className="text-[color:var(--muted)] hover:text-[color:var(--brand)] transition-colors">{company.phone}</a>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <span className="w-11 h-11 rounded-full bg-[color:var(--brand-soft)] text-[color:var(--brand)] inline-flex items-center justify-center"><Mail size={18} /></span>
                <div>
                  <div className="font-semibold">Email</div>
                  <a href={`mailto:${company.email}`} className="text-[color:var(--muted)] hover:text-[color:var(--brand)] transition-colors">{company.email}</a>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <span className="w-11 h-11 rounded-full bg-[color:var(--brand-soft)] text-[color:var(--brand)] inline-flex items-center justify-center"><User size={18} /></span>
                <div>
                  <div className="font-semibold">Directors</div>
                  <div className="text-[color:var(--muted)]">Palak Shah & Krupali Shah</div>
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-md overflow-hidden shadow-lg aspect-[16/9] border border-[color:var(--line)]">
              <img
                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=1200&auto=format&fit=crop"
                alt="Vedyah Exim partnership handshake"
                className="w-full h-full object-cover"
                data-testid="contact-partnership-image"
              />
            </div>
          </div>

          <form onSubmit={submit} className="bg-[color:var(--brand-soft)]/30 p-8 rounded-md border border-[color:var(--line)] space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input required value={form.name} onChange={set('name')} placeholder="Your Name" className="h-12 px-4 rounded-md border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition" />
              <input required type="email" value={form.email} onChange={set('email')} placeholder="Your Email" className="h-12 px-4 rounded-md border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input value={form.phone} onChange={set('phone')} placeholder="Phone (optional)" className="h-12 px-4 rounded-md border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition" />
              <input value={form.subject} onChange={set('subject')} placeholder="Subject" className="h-12 px-4 rounded-md border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition" />
            </div>
            <textarea required value={form.message} onChange={set('message')} placeholder="Your Message" rows="5" className="w-full px-4 py-3 rounded-md border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition" />
            <button disabled={loading} type="submit" className="h-12 px-6 rounded-md bg-[color:var(--brand)] text-white flex items-center gap-2 hover:bg-[color:var(--brand-2)] transition-colors disabled:opacity-60">
              <Send size={16} /> {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      <section className="py-16 bg-[color:var(--brand-soft)]/25 border-t border-[color:var(--line)]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-[color:var(--brand)] uppercase tracking-[0.4em] text-xs mb-3">Find Us</p>
            <h2 className="text-3xl md:text-4xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>Visit Our Offices</h2>
            <div className="mx-auto w-16 h-[2px] bg-[color:var(--brand)] mt-4" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-[color:var(--ink)] mb-3">Head Office — Surat</h3>
              <MapEmbed query="Sharda Sadan, Near Central Bus Stand, Railway Station Road, Surat, Gujarat 395003" title="Head Office" height={320} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[color:var(--ink)] mb-3">Forwarding Office — Hazira</h3>
              <MapEmbed query="Adani Port Road, Hazira, Choryashi, Surat, Gujarat 394270" title="Hazira Office" height={320} />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
